/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import clsx from 'clsx'
import {CheckIcon, XMarkIcon} from '@heroicons/react/24/solid'
import {ArrowLongRightIcon} from '@heroicons/react/24/outline'
import {AntdRangePickerComponent} from 'components/Ui/AntdPickerWrapper'
import dayjs, {Dayjs} from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {useTranslation} from 'react-i18next'
import {useDebouncedCallback} from 'use-debounce'

import {DATE_FORMATS} from 'constants/DateFormats'
import {useUiTheme} from 'theme/UiThemeProvider'

import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/en'
import 'dayjs/locale/nl'
import 'dayjs/locale/sk'
import 'dayjs/locale/hu'
import 'dayjs/locale/it'

dayjs.extend(customParseFormat)

const TIME_PARSE_FORMATS = [DATE_FORMATS.TIME, 'HH:mm:ss', 'H:mm'] as const

interface UiTableTimeRangeFilterProps {
    value: { from?: string; to?: string }
    onChange: (value: { from?: string; to?: string } | undefined) => void
    debounceTime?: number
    className?: string
}

const parseTimeValue = (value?: string): Dayjs | null => {
    if (!value?.trim()) {
        return null
    }

    const parsed = dayjs(value.trim(), [...TIME_PARSE_FORMATS], true)
    return parsed.isValid() ? parsed : null
}

const toPickerRange = (
    from?: string,
    to?: string
): [Dayjs | null, Dayjs | null] | null => {
    const start = parseTimeValue(from)
    const end = parseTimeValue(to)

    if (!start && !end) {
        return null
    }

    return [start, end]
}

const UiTableTimeRangeFilter: React.FC<UiTableTimeRangeFilterProps> = ({
    value,
    onChange,
    debounceTime = 300,
    className,
}) => {
    const { t } = useTranslation()
    const { mode } = useUiTheme()
    const isDark = mode === 'dark'

    const [fromValue, setFromValue] = useState<string>(value?.from ?? '')
    const [toValue, setToValue] = useState<string>(value?.to ?? '')
    const [isDirty, setIsDirty] = useState(false)
    const [isFocused, setIsFocused] = useState(false)

    const externalFromValue = value?.from ?? ''
    const externalToValue = value?.to ?? ''
    const effectiveFromValue = isDirty ? fromValue : externalFromValue
    const effectiveToValue = isDirty ? toValue : externalToValue
    const effectiveRange = toPickerRange(effectiveFromValue, effectiveToValue)
    const hasValue = Boolean(effectiveFromValue || effectiveToValue)

    const emitChange = (fromRaw: string, toRaw: string) => {
        if (!fromRaw && !toRaw) {
            onChange(undefined)
            return
        }

        onChange({
            from: fromRaw || undefined,
            to: toRaw || undefined,
        })
    }

    const debouncedEmitChange = useDebouncedCallback(
        (fromRaw: string, toRaw: string) => emitChange(fromRaw, toRaw),
        debounceTime
    )
    const shouldDebounceChanges = debounceTime > 0

    const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        const nextFrom = dates?.[0]?.format(DATE_FORMATS.TIME) ?? ''
        const nextTo = dates?.[1]?.format(DATE_FORMATS.TIME) ?? ''

        setFromValue(nextFrom)
        setToValue(nextTo)
        setIsDirty(true)

        if (shouldDebounceChanges) {
            debouncedEmitChange(nextFrom, nextTo)
        }
    }

    const handleApply = () => {
        if (shouldDebounceChanges) {
            debouncedEmitChange.cancel()
        }
        emitChange(effectiveFromValue, effectiveToValue)
        setIsDirty(false)
    }

    const handleClear = () => {
        if (shouldDebounceChanges) {
            debouncedEmitChange.cancel()
        }
        setFromValue('')
        setToValue('')
        onChange(undefined)
        setIsDirty(false)
    }

    const pickerClassName = clsx(
        'UiTableTimeRangeFilter-picker flex-1! min-w-0!',
        hasValue
            ? '[&_.ant-picker-input>input]:text-blue-500! [&_.ant-picker-input>input]:font-semibold!'
            : '[&_.ant-picker-input>input]:text-gray-400! dark:[&_.ant-picker-input>input]:text-gray-100!',
        '[&_.ant-picker-input>input]:placeholder:text-gray-400! dark:[&_.ant-picker-input>input]:placeholder:text-gray-500!',
        '[&_.ant-picker-clear]:hidden!'
    )

    return (
        <div
            className={clsx(
                'UiTableTimeRangeFilter flex flex-row gap-0.5 items-center w-full max-w-full h-7 relative bg-white/50 border border-gray-300/80 rounded-md transition-all duration-150 focus-within:ring-1 focus-within:ring-blue-400 focus-within:bg-white dark:bg-gray-900/50 dark:border-gray-700/80 dark:focus-within:bg-gray-900 font-normal mt-1.5 px-1 min-w-0',
                isFocused && 'ring-1 ring-blue-400 bg-white dark:bg-gray-900',
                className
            )}
            data-debounce-time={debounceTime}
            onClick={(e) => e.stopPropagation()}
        >
            <AntdRangePickerComponent
                picker="time"
                size="small"
                variant="borderless"
                format={DATE_FORMATS.TIME}
                use12Hours={false}
                minuteStep={10}
                hideDisabledOptions
                allowEmpty={[true, true]}
                allowClear={false}
                placeholder={[t('Time From'), t('Time To')]}
                separator={
                    <ArrowLongRightIcon className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
                }
                suffixIcon={null}
                className={pickerClassName}
                style={{ borderWidth: 0, boxShadow: 'none' }}
                value={effectiveRange}
                onChange={handleRangeChange}
                onOpenChange={setIsFocused}
                disabledTime={() => ({ disabledHours: () => [0, 1, 2, 3, 4, 5] })}
                classNames={{
                    popup: {
                        root: 'dark:ant-picker-dropdown-dark',
                    },
                }}
                getPopupContainer={() => document.body}
            />
            {hasValue && (
                <button
                    className={clsx(
                        'flex items-center justify-center px-1 py-0.5 rounded transition-all duration-150 flex-none',
                        'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                    )}
                    onClick={handleClear}
                    type="button"
                    title={t('Clear')}
                >
                    <XMarkIcon className="w-3.5 h-3.5" />
                </button>
            )}
            <button
                className={clsx(
                    'flex items-center justify-center px-1 py-0.5 rounded transition-all duration-150 flex-none',
                    hasValue
                        ? 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 cursor-pointer'
                        : 'bg-gray-300 text-gray-400 dark:bg-gray-700 dark:text-gray-600 cursor-not-allowed'
                )}
                onClick={handleApply}
                type="button"
                disabled={!hasValue}
                title={t('Apply filter')}
            >
                <CheckIcon className="w-3.5 h-3.5" />
            </button>
        </div>
    )
}

export default UiTableTimeRangeFilter
